import toast from 'react-hot-toast';
import dayjs from 'dayjs';
type ToastType = 'success' | 'error' | 'info' | 'warning';

// Store the last toast ID
let currentToastId: string | undefined;

interface ToastOptions {
  title?: string;
  duration?: number;
}

export const showToastMessage = (type: ToastType, message: string, options: ToastOptions = {}) => {
  if (currentToastId) toast.dismiss(currentToastId);

  currentToastId = toast(
    // pass structured data — CustomToast reads it from t.message
    { type, message, title: options.title } as any,
    {
      id: 'unique-toast',
      duration: options.duration ?? 3000,
    }
  );

  return currentToastId;
};

// convenience for FCM foreground notifications
export const showNotificationToast = (title: string, body: string) => {
  if (currentToastId) toast.dismiss(currentToastId);

  currentToastId = toast({ type: 'notification', message: body, title } as any, {
    id: 'unique-toast',
    duration: 5000,
  });

  return currentToastId;
};
export const handleApiError = (err: unknown, fallbackMsg = 'Something went wrong') => {
  console.error('API Error:', err);

  let message = fallbackMsg;

  // Direct string error
  if (typeof err === 'string') {
    message = err;
  }

  // Native Error object
  else if (err instanceof Error) {
    message = err.message;
  }

  // RTK Query / Axios / Backend
  else if (typeof err === 'object' && err !== null) {
    const errorObj = err as any;

    if (typeof errorObj?.data?.message === 'string') {
      message = errorObj.data.message;
    } else if (Array.isArray(errorObj?.data?.message)) {
      message = errorObj.data.message[0];
    } else if (Array.isArray(errorObj?.data?.errors)) {
      message = errorObj.data.errors[0];
    } else if (typeof errorObj?.message === 'string') {
      message = errorObj.message;
    } else if (typeof errorObj?.error === 'string') {
      message = errorObj.error;
    }
  }

  showToastMessage('error', message);

  return message;
};
export const getPlaceSuffix = (place: number) => {
  if (place % 100 >= 11 && place % 100 <= 13) {
    return 'th';
  }

  switch (place % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};
export const getCountdown = (
  startTime: string,
  labels: {
    in?: string;
    at?: string;
    started?: string;
  } = {}
) => {
  const {
    in: inLabel = 'Starts in',
    at: atLabel = 'Starts at',
    started: startedLabel = 'Started',
  } = labels;

  const now = dayjs();
  const matchTime = dayjs(startTime);

  const difference = matchTime.diff(now);

  if (difference <= 0) {
    return {
      expired: true,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      formatted: startedLabel,
    };
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  const diffMinutes = matchTime.diff(now, 'minute');

  let formatted = '';

  if (diffMinutes > 0 && diffMinutes <= 120) {
    const hrs = Math.floor(diffMinutes / 60);
    const mins = diffMinutes % 60;

    formatted = hrs > 0 ? `${inLabel} ${hrs} hr ${mins} min` : `${inLabel} ${mins} min`;
  } else if (matchTime.isSame(now, 'day')) {
    formatted = `${atLabel} ${matchTime.format('HH:mm')}`;
  } else {
    formatted = `${atLabel} ${matchTime.format('DD MMM HH:mm')}`;
  }

  return {
    expired: false,
    days,
    hours,
    minutes,
    seconds,
    formatted,
  };
};
export const cleanParams = <T extends Record<string, any>, K extends keyof T = never>(
  params: T,
  options?: {
    excludeKeys?: K[];
    withTimestamp?: boolean;
  }
): Partial<Omit<T, K>> & { ts?: number } => {
  const { excludeKeys = [], withTimestamp = true } = options || {};

  const filtered = Object.fromEntries(
    Object.entries(params).filter(([key, value]) => {
      if (excludeKeys.includes(key as K)) return false;

      return value !== undefined && value !== null && value !== '';
    })
  ) as Partial<Omit<T, K>>;

  if (withTimestamp) {
    return {
      ...filtered,
      ts: Date.now(),
    };
  }

  return filtered;
};
export const formatSportName = (sport?: string) => {
  if (!sport) return '';

  return sport.charAt(0).toUpperCase() + sport.slice(1).toLowerCase();
};
export const formatReadableDateTime = (
  isoString: string | Date,
  format: 'default' | 'compact' = 'default'
) => {
  const date = new Date(isoString);

  if (format === 'compact') {
    return date
      .toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      .replace(',', '');
  }

  // existing format
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};
